import { useRoute } from '#imports'

import type { BuybackOffer } from '@backmarket/http-api/src/api-models/BuybackOffer'
import type { InsuranceOffer } from '@backmarket/http-api/src/api-models/InsuranceOffer'
import type { PaymentMethodCode } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type { Currency } from '@backmarket/http-api/src/standards/Currency'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { formatPrice } from '@backmarket/nuxt-module-tracking/formatPrice'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { capitalize } from '@backmarket/utils/string/capitalize'
import { cleanAlgoliaLabel } from '@backmarket/utils/string/cleanAlgoliaLabel'

import type {
  CartItem,
  CartSummary,
} from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { DEFAULT_INSURANCE_ID } from '@backmarket/nuxt-layer-checkout/constants'
import {
  type InsuranceBillingType,
  getInsuranceBillingType,
} from '@backmarket/nuxt-layer-checkout/utils/tracking'

function getPaymentType(insurance?: InsuranceOffer) {
  if (!insurance || insurance.id.toString() === DEFAULT_INSURANCE_ID) return ''

  return insurance.paymentFrequency
}

/**
 * Returns the GA4 `item_insurance_type` label for a cart insurance offer.
 * Returns `''` when no offer is selected or the default "no insurance" offer
 * is selected; otherwise delegates to the shared
 * {@link getInsuranceBillingType} mapper.
 */
function mapInsuranceBillingType(
  insurance?: InsuranceOffer,
): InsuranceBillingType {
  if (!insurance || insurance.id.toString() === DEFAULT_INSURANCE_ID) return ''

  return getInsuranceBillingType(insurance.paymentFrequency)
}

/**
 * Maps payment method code to installment information.
 * Returns installment status and count based on payment method type.
 */
function getPaymentInstallmentInfo(
  paymentMethodCode: PaymentMethodCode | null,
): {
  isInstallment: boolean
  count: number
} {
  if (!paymentMethodCode) return { isInstallment: false, count: 0 }

  // Map payment method codes to installment counts
  const installmentMap: Partial<Record<PaymentMethodCode, number>> = {
    oney3x: 3,
    oney4x: 4,
    oney6x: 6,
    oney10x: 10,
    oney12x: 12,
    klarna_slice_it: 3,
    scalapay: 3,
    scalapay3x: 3,
  }

  const count = installmentMap[paymentMethodCode] ?? 0

  return {
    isInstallment: count > 0,
    count,
  }
}

/**
 * Extracts shipping tier from cart items.
 * Uses the first item's shipping display name as the tier.
 */
function getShippingTier(items: Array<CartItem>): string {
  if (items.length === 0) return 'Standard Shipping'

  const firstItem = items[0]

  return firstItem?.shippingSelected?.shipperDisplay ?? 'Standard Shipping'
}

/**
 * Extracts payment surcharge from cart summary.
 * Returns 0 if no service fee is present.
 */
function getPaymentSurcharge(summary: CartSummary | null | undefined): number {
  return summary?.serviceFee?.amount ? Number(summary.serviceFee.amount) : 0
}

export const getTrackingProduct = (item: CartItem, currency: Currency) => {
  const insurance = item.insuranceOffers?.find(
    (offer) => offer.id.toString() !== DEFAULT_INSURANCE_ID && offer.selected,
  )

  const deliveryOption = item.options.find(
    (option) => option.type === 'delivery',
  )
  const deliveryOptionChoices = deliveryOption?.choices || []

  return {
    available: item.available === 'yes',
    brand: item.brand ?? '',
    category: item.category ?? '',
    color: item.color ?? '',
    currency,
    id: item.productId ?? '',
    insurancePrice: insurance?.price.amount ?? '',
    insuranceTitle: insurance?.title ?? '',
    insurancePaymentType: getPaymentType(insurance),
    listingId: item.listingId ?? '',
    merchantId: item.merchantPublicId ?? item.merchantId ?? '',
    model: item.model ?? '',
    name: item.title ?? '',
    specialOfferType: item.specialOfferType,
    price: item.price ?? '',
    previousPrice: item.previousPrice?.amount ?? '',
    quantity: item.quantity ?? '',
    quantityMax: item.quantityMax ?? '',
    collectionPointAvailable: item.options.some((option) =>
      option.choices.some((choice) => choice.isCollectionPoint),
    ),
    shipper: [
      item.shippingSelected.shipperId ?? '',
      item.shippingSelected.shippingFeeDelay ?? '0',
      formatPrice({
        amount: item.shippingSelected.shippingFeePrice,
        currency,
      }),
    ].join(' - '),
    shippingDelay: item.shippingSelected.shippingFeeDelay ?? '',
    shippingPrice: item.shippingSelected.shippingFeePrice ?? '',
    shippingOptions: deliveryOptionChoices.map((choice) =>
      [
        choice.shipperId ?? '',
        choice.shippingDelay ?? '0',
        formatPrice({
          amount: choice.price,
          currency,
        }),
      ].join(' - '),
    ),
    uuid: item.akeneoId ?? '',
    variant: item.gradeId ?? '',
    warrantyDuration: item.warranties[0]?.delay ?? '',

    dealType: 'normal',
    isInsuranceEligible: !!item.insuranceOffers?.length || false,
    insuranceEligibleOffers:
      item.insuranceOffers &&
      item.insuranceOffers
        .filter((offer) => offer.id.toString() !== DEFAULT_INSURANCE_ID)
        .map((offer) => offer.paymentFrequency)
        .join('-'),
    mobilePlanOfferSelected: item.mobilePlan?.selectedOffer.name ?? '',
    mobilePlanOfferSelectedType: item.mobilePlan?.selectedOffer.type ?? '',
    mobilePlanPartner: item.mobilePlan?.selectedOffer.partner.name ?? '',
    isFlashSales: (item.promoCodes?.length ?? 0) > 0,
    campaignId: item.adjustmentFeeCampaignId ?? '',
    populationId: item.adjustmentFeePopulationId ?? '',
  }
}

export const toViewCartItem = (
  item: CartItem,
  locationId: string,
  index: number,
) => {
  const discount = Math.max(item.formerPrice - item.price, 0)
  return {
    item_id: item.productId ?? '',
    item_uuid: item.akeneoId ?? '',
    item_name: item.title ?? '',
    affiliation: item.merchantPublicId ?? String(item.merchantId),
    discount,
    index,
    item_brand: capitalize(cleanAlgoliaLabel(item.brand ?? '')),
    price: item.price,
    quantity: item.quantity,
    item_category: item.category ?? '',
    item_variant: item.gradeName ?? '',
    item_warranty_duration: item.warranties[0]?.delay?.toString() ?? '',
    item_mobile_plan_name: item.mobilePlan?.selectedOffer.name ?? '',
    item_mobile_plan_carrier: item.mobilePlan?.selectedOffer.partner.name ?? '',
    location_id: locationId,
    google_business_vertical: 'retail',
    item_deal_type: item.specialOfferType?.toString() ?? '',
    item_units_in_stock: item.quantityMax,
    // TODO [BB-28.1]: item_average_rating, item_review_count, seller_average_rating, seller_review_count — not on CartItem (needs product/seller ratings API)
    // TODO [BB-28.2]: item_variant2 through item_variant15 — only grade variant is available on CartItem
    // TODO [BB-28.3]: item_category2 through item_category5 — only L1 category on CartItem
    // TODO [BB-28.4]: item_list_id, item_list_name — original add-to-cart source not persisted on CartItem
    // TODO [BB-28.5]: item_has_free_return, item_free_return_window, item_is_favorited, item_finance_available, item_finance_provider, item_is_trade_in_eligible — not on CartItem
    // TODO [BB-28.6]: item_mobile_plan_data_allowance, item_mobile_plan_commitment_months, item_mobile_plan_monthly_price — not on MobilePlanOffer
  }
}

export const withInsuranceTrackingFields = (item: CartItem) => {
  const insurance = item.insuranceOffers?.find(
    (offer) => offer.id.toString() !== DEFAULT_INSURANCE_ID && offer.selected,
  )

  return {
    item_insurance_name: insurance?.title ?? 'none',
    item_insurance_type: mapInsuranceBillingType(insurance),
    item_insurance_price: insurance ? Number(insurance.price.amount) : 0,
    item_insurance_currency: insurance?.price.currency ?? '',
    item_insurance_term_months: insurance?.totalCoverageDurationInMonths ?? 0,
  }
}

/**
 * Maps a cart item for the `add_payment_info` event. Shares all `view_cart`
 * fields and adds insurance and per-item shipping fields.
 */
export const toAddPaymentInfoItem = (
  item: CartItem,
  locationId: string,
  index: number,
) => {
  const shipping =
    'shippingFeeDelay' in item.shippingSelected ? item.shippingSelected : null

  return {
    ...toViewCartItem(item, locationId, index),
    ...withInsuranceTrackingFields(item),
    item_shipping_method: shipping?.shipperDisplay ?? '',
    item_shipping_cost: shipping ? Number(shipping.shippingFeePrice) : 0,
  }
}

/**
 * Maps a cart item for the `add_shipping_info` event. Shares all `view_cart`
 * fields and adds insurance and per-item shipping fields.
 */
const toAddShippingInfoItem = (
  item: CartItem,
  locationId: string,
  index: number,
) => {
  const shipping =
    'shippingFeeDelay' in item.shippingSelected ? item.shippingSelected : null

  return {
    ...toViewCartItem(item, locationId, index),
    ...withInsuranceTrackingFields(item),
    item_shipping_cost: shipping ? Number(shipping.shippingFeePrice) : 0,
    item_shipping_method: shipping?.shipperDisplay ?? '',
    // TODO [BB-31.1]: item_shipping_method should be a technical enum (standard/express/overnight) — only the shipperDisplay label is available on SelectedShipping
  }
}

/**
 * Builds the event-level `trade_in_details` object shared by `view_cart`,
 * `begin_checkout`, `add_payment_info`, and `add_shipping_info`. Returns an
 * empty object when there is no swap offer, so it can be spread directly into
 * the event payload.
 */
const buildTradeInDetails = (
  swapOffer: BuybackOffer | null,
  currency: Currency,
): Record<string, Record<string, unknown>> => {
  if (isEmpty(swapOffer) || !swapOffer) return {}

  return {
    trade_in_details: {
      trade_in_id: swapOffer.sourcingListingPublicId ?? '',
      trade_in_item_category:
        (swapOffer.diagnosticPayload?.category as string) ?? '',
      trade_in_price: swapOffer.price,
      trade_in_currency: currency,
      // TODO [BB-28.7]: trade_in_item_brand, trade_in_item_name, trade_in_shipping_method, trade_in_payout_method, trade_in_payout_timing, trade_in_questions[] — not on BuybackOffer
    },
  }
}

const toRemoveFromCartItem = (
  item: CartItem,
  locationId: string,
  index: number,
  removedQuantity?: number,
) => {
  const discount = Math.max(item.formerPrice - item.price, 0)

  return {
    item_id: item.productId ?? '',
    item_uuid: item.akeneoId ?? '',
    item_name: item.title ?? '',
    affiliation: item.merchantPublicId ?? item.merchant ?? '',
    discount,
    index,
    item_brand: capitalize(cleanAlgoliaLabel(item.brand ?? '')),
    price: item.price,
    quantity: removedQuantity ?? item.quantity,
    item_category: item.category ?? '',
    item_variant: item.gradeName ?? '',
    item_warranty_duration: item.warranties[0]?.delay?.toString() ?? '',
    item_mobile_plan_name: item.mobilePlan?.selectedOffer.name ?? '',
    item_mobile_plan_carrier: item.mobilePlan?.selectedOffer.partner.name ?? '',
    location_id: locationId,
    google_business_vertical: 'retail',
    // TODO [BB-29.2]: item_variant2 through item_variant15 — only grade variant is available on CartItem
    // TODO [BB-29.3]: item_category2 through item_category5 — only L1 category on CartItem
    // TODO [BB-29.4]: item_list_id, item_list_name — original add-to-cart source not persisted on CartItem
    // TODO [BB-29.5]: item_has_free_return, item_free_return_window, item_is_favorited, item_finance_available, item_finance_provider, item_is_trade_in_eligible — not on CartItem
    // TODO [BB-29.6]: item_mobile_plan_data_allowance, item_mobile_plan_commitment_months, item_mobile_plan_monthly_price — not on MobilePlanOffer
  }
}

export function useCheckoutTracking() {
  const route = useRoute()
  const {
    trackFunnel: track,
    trackViewCart: trackViewCartEvent,
    trackBeginCheckout: trackBeginCheckoutEvent,
    trackAddPaymentInfo: trackAddPaymentInfoEvent,
    trackRemoveFromCartGa4: trackRemoveFromCartEvent,
    trackAddShippingInfo: trackAddShippingInfoEvent,
  } = useTracking()
  const { defaultCurrency, countryCode } = useMarketplace().market

  const trackFunnel = (
    items: Array<CartItem>,
    swapOffer: BuybackOffer | null,
  ) => {
    track({
      products: items.map((item) => getTrackingProduct(item, defaultCurrency)),
      swap: !isEmpty(swapOffer),
      swapCategory: !isEmpty(swapOffer)
        ? (swapOffer?.diagnosticPayload?.category as string)
        : null,
      swapEstimation: !isEmpty(swapOffer) ? swapOffer.price : null,
      step: route.meta?.gtmStepId ?? null,
      pageType: (route.name as string) ?? '',
    })
  }

  const cartValue = (items: Array<CartItem>) =>
    items.reduce((sum, item) => sum + item.price * item.quantity, 0)

  const trackViewCart = (
    items: Array<CartItem>,
    swapOffer: BuybackOffer | null,
  ) => {
    trackViewCartEvent({
      has_trade_in: !isEmpty(swapOffer),
      ecommerce: {
        currency: defaultCurrency,
        value: cartValue(items),
        items: items.map((item, index) =>
          toViewCartItem(item, countryCode, index),
        ),
      },
      ...buildTradeInDetails(swapOffer, defaultCurrency),
    })
  }

  const trackBeginCheckout = (
    items: Array<CartItem>,
    swapOffer: BuybackOffer | null,
  ) => {
    trackBeginCheckoutEvent({
      has_trade_in: !isEmpty(swapOffer),
      ecommerce: {
        currency: defaultCurrency,
        value: cartValue(items),
        items: items.map((item, index) => ({
          ...toViewCartItem(item, countryCode, index),
          ...withInsuranceTrackingFields(item),
        })),
      },
      ...buildTradeInDetails(swapOffer, defaultCurrency),
    })
  }

  const trackRemoveFromCart = (
    item: CartItem,
    index: number,
    swapOffer: BuybackOffer | null,
    removedQuantity?: number,
  ) => {
    const quantity = removedQuantity ?? item.quantity
    const value = item.price * quantity

    trackRemoveFromCartEvent({
      has_trade_in: !isEmpty(swapOffer),
      ecommerce: {
        currency: defaultCurrency,
        value,
        items: [toRemoveFromCartItem(item, countryCode, index, quantity)],
      },
      ...(!isEmpty(swapOffer) && swapOffer
        ? {
            trade_in_details: {
              trade_in_id: swapOffer.sourcingListingPublicId ?? '',
              trade_in_item_category:
                (swapOffer.diagnosticPayload?.category as string) ?? '',
              trade_in_item_brand:
                (swapOffer.diagnosticPayload?.brand as string) ?? '',
              trade_in_item_name: swapOffer.title ?? '',
              trade_in_price: swapOffer.price,
              trade_in_currency: defaultCurrency,
              // TODO [BB-29.7]: trade_in_shipping_method, trade_in_payout_method, trade_in_payout_timing, trade_in_questions[] — not on BuybackOffer or needs mapping
            },
          }
        : {}),
    })
  }

  const trackAddPaymentInfo = (
    items: Array<CartItem>,
    summary: CartSummary | null,
    paymentMethodCode: PaymentMethodCode | null,
    swapOffer: BuybackOffer | null,
  ) => {
    const { isInstallment, count: installmentCount } =
      getPaymentInstallmentInfo(paymentMethodCode)
    const shippingTier = getShippingTier(items)
    const paymentSurcharge = getPaymentSurcharge(summary)

    trackAddPaymentInfoEvent({
      has_trade_in: !isEmpty(swapOffer),
      payment_is_installment: isInstallment,
      payment_installment_count: installmentCount,
      payment_surcharge: paymentSurcharge,
      ecommerce: {
        currency: defaultCurrency,
        value: cartValue(items),
        shipping: summary?.totalDelivery?.amount
          ? Number(summary.totalDelivery.amount)
          : 0,
        shipping_tier: shippingTier,
        payment_type: paymentMethodCode ?? '',
        items: items.map((item, index) =>
          toAddPaymentInfoItem(item, countryCode, index),
        ),
      },
      ...buildTradeInDetails(swapOffer, defaultCurrency),
    })
  }

  const trackAddShippingInfo = (
    items: Array<CartItem>,
    swapOffer: BuybackOffer | null,
  ) => {
    const shipping = items.reduce((sum, item) => {
      const itemShipping =
        'shippingFeeDelay' in item.shippingSelected
          ? item.shippingSelected
          : null

      return (
        sum +
        (itemShipping ? Number(itemShipping.shippingFeePrice) : 0) *
          item.quantity
      )
    }, 0)

    const firstShipping = items
      .map((item) => item.shippingSelected)
      .find((selected) => 'shipperDisplay' in selected)
    const shippingTier =
      firstShipping && 'shipperDisplay' in firstShipping
        ? firstShipping.shipperDisplay
        : ''

    trackAddShippingInfoEvent({
      has_trade_in: !isEmpty(swapOffer),
      ecommerce: {
        currency: defaultCurrency,
        value: cartValue(items),
        shipping,
        shipping_tier: shippingTier,
        items: items.map((item, index) =>
          toAddShippingInfoItem(item, countryCode, index),
        ),
      },
      ...buildTradeInDetails(swapOffer, defaultCurrency),
    })
  }

  return {
    trackFunnel,
    trackViewCart,
    trackBeginCheckout,
    trackAddPaymentInfo,
    trackRemoveFromCart,
    trackAddShippingInfo,
  }
}
